import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { UseFormReturn, FieldValues, Path, PathValue } from "react-hook-form";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Combobox,
} from "@altimateai/lego";
import { useInfiniteQuery, keepPreviousData } from "@tanstack/react-query";
import {
  getDataEntityType,
  fetchWarehouses,
  fetchDatabases,
  fetchSchemas,
  fetchRoles,
  fetchUsers,
  fetchDatabricksAccounts,
  fetchDatabricksWorkspaces,
} from "./AsyncEntitySelectorApi";

interface AsyncEntitySelectorProps<TFieldValues extends FieldValues = FieldValues> {
  entityType?: string;
  staticOptions?: { label: string; value: string }[];
  isMultiSelect: boolean;
  form: UseFormReturn<TFieldValues>;
  fieldName: Path<TFieldValues>; // e.g., "rules.${index}.entity" or "ownerships.${index}.condition_value"
  label?: string;
}

const AsyncEntitySelector = <TFieldValues extends FieldValues = FieldValues>({
  entityType,
  staticOptions = [],
  isMultiSelect,
  form,
  fieldName,
  label = "Entity",
}: AsyncEntitySelectorProps<TFieldValues>) => {
  const dataEntityType = getDataEntityType(entityType);
  const useStaticOptions = !dataEntityType && staticOptions.length > 0;
  const [searchTerm, setSearchTerm] = useState("");
  const searchTermRef = useRef(searchTerm);
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastEntityTypeRef = useRef<string | undefined>(entityType);

  // Keep ref in sync with state
  useEffect(() => {
    searchTermRef.current = searchTerm;
  }, [searchTerm]);

  // Reset when entity type changes
  useEffect(() => {
    if (lastEntityTypeRef.current === entityType) return;
    lastEntityTypeRef.current = entityType;
    setSearchTerm("");
    searchTermRef.current = "";
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
      debounceTimeoutRef.current = null;
    }
  }, [entityType]);

  const handleValueChange = useCallback(
    (value: string | string[]) => {
      const newValue = isMultiSelect
        ? (Array.isArray(value) ? value : [value]).join(",")
        : Array.isArray(value)
          ? value[0]
          : value;
      form.setValue(fieldName, newValue as PathValue<TFieldValues, Path<TFieldValues>>);
    },
    [isMultiSelect, form, fieldName]
  );

  const getComboboxValue = useCallback((): string | string[] => {
    const currentValue = form.getValues(fieldName) as string;
    return !currentValue
      ? isMultiSelect
        ? []
        : ""
      : isMultiSelect
        ? currentValue.split(",").filter(v => v.trim())
        : currentValue;
  }, [form, fieldName, isMultiSelect]);

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteQuery({
    queryKey: ["async-entity-selector", dataEntityType, searchTerm],
    initialPageParam: 1,
    queryFn: async ({ pageParam }) => {
      if (!dataEntityType) {
        return { items: [], pages: 1, currentPage: 1 };
      }

      switch (dataEntityType) {
        case "warehouse":
          return fetchWarehouses(searchTerm, pageParam);
        case "database":
          return fetchDatabases(searchTerm, pageParam);
        case "schema":
          return fetchSchemas(searchTerm, pageParam);
        case "role":
          return fetchRoles(searchTerm, pageParam);
        case "user":
          return fetchUsers(searchTerm, pageParam);
        case "account":
          return fetchDatabricksAccounts(searchTerm, pageParam);
        case "workspace":
          return fetchDatabricksWorkspaces(searchTerm, pageParam);
        default:
          return { items: [], pages: 1, currentPage: pageParam };
      }
    },
    getNextPageParam: lastPage =>
      lastPage.currentPage < lastPage.pages ? lastPage.currentPage + 1 : undefined,
    // Auto-fetch when we have a valid data entity type so initial options are available
    enabled: Boolean(dataEntityType),
    // Keep previous data while fetching new data to prevent flickering and unnecessary re-renders
    placeholderData: keepPreviousData,
  });

  const apiOptions = useMemo(() => {
    if (!data?.pages) return [];
    return data.pages.flatMap(page => page.items);
  }, [data]);

  // Refs for values needed by stable callbacks (pagination)
  const dataRef = useRef(data);
  const hasNextPageRef = useRef(hasNextPage);
  const isFetchingNextPageRef = useRef(isFetchingNextPage);
  const fetchNextPageRef = useRef(fetchNextPage);

  useEffect(() => {
    dataRef.current = data;
    hasNextPageRef.current = hasNextPage;
    isFetchingNextPageRef.current = isFetchingNextPage;
    fetchNextPageRef.current = fetchNextPage;
  }, [data, hasNextPage, isFetchingNextPage, fetchNextPage]);

  // Stable handleLoadMore - never changes reference.
  // This is critical because the Combobox has a useEffect([onLoadMore]) that
  // calls onLoadMore("", 1) whenever onLoadMore changes. If handleLoadMore
  // changed on every render, this effect would constantly reset the search.
  const handleLoadMore = useCallback(
    async (searchValue?: string, requestedPage?: number) => {
      // Handle search term updates
      if (typeof searchValue === "string") {
        // Clear any pending debounce
        if (debounceTimeoutRef.current) {
          clearTimeout(debounceTimeoutRef.current);
          debounceTimeoutRef.current = null;
        }

        // Skip if same as current search term
        if (searchValue === searchTermRef.current) {
          return;
        }

        // For empty search, update immediately (no debounce delay)
        if (searchValue === "") {
          searchTermRef.current = "";
          setSearchTerm("");
          return;
        }

        // Debounce non-empty search term updates
        debounceTimeoutRef.current = setTimeout(() => {
          searchTermRef.current = searchValue;
          setSearchTerm(searchValue);
          debounceTimeoutRef.current = null;
        }, 300);
        return;
      }

      // Handle pagination (when no searchValue is provided)
      const pages = dataRef.current?.pages;
      const currentPage =
        pages && pages.length > 0 ? (pages[pages.length - 1]?.currentPage ?? 0) : 0;
      if (
        requestedPage !== undefined &&
        requestedPage === currentPage + 1 &&
        hasNextPageRef.current &&
        !isFetchingNextPageRef.current
      ) {
        await fetchNextPageRef.current();
      }
    },
    [] // Empty deps - stable reference prevents Combobox effect re-firing
  );

  // Stable handlePageReset - just clears the debounce timer.
  // The Combobox calls onPageReset() then onLoadMore(searchValue, 1) on each
  // keystroke. We only need to cancel the previous debounce here; the actual
  // search term update is handled by handleLoadMore via onLoadMore.
  const handlePageReset = useCallback(() => {
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
      debounceTimeoutRef.current = null;
    }
  }, []);

  const comboboxOptions = useStaticOptions ? staticOptions : apiOptions;

  return (
    <FormField
      control={form.control}
      name={fieldName}
      render={() => (
        <FormItem>
          <FormLabel>{label}</FormLabel>
          <FormControl>
            <Combobox
              options={comboboxOptions}
              value={getComboboxValue()}
              onChange={handleValueChange}
              placeholder={isMultiSelect ? "Select entities..." : "Select entity..."}
              searchPlaceholder={"Search fields..."}
              multiSelect={isMultiSelect}
              showApplyButton={false}
              showClearButton={isMultiSelect}
              buttonProps={{
                className: "al-w-full",
                "aria-label": isMultiSelect ? "Select entities" : "Select entity",
              }}
              {...(dataEntityType && {
                onLoadMore: handleLoadMore,
                hasMore: Boolean(hasNextPage),
                onPageReset: handlePageReset,
              })}
            />
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );
};
export default AsyncEntitySelector;
